import axios from 'axios';
import { CARTRIDGES, getCartridgeAbbrev } from './cartridges';

// Compute API base URL with runtime safeguards to avoid localhost in production
const computeApiBaseUrl = () => {
  // Precedence: localStorage override -> window global -> build-time env -> same-origin '/api'
  let ls = null;
  try {
    ls = typeof window !== 'undefined' ? (localStorage.getItem('apiBaseUrl') || null) : null;
  } catch {}
  const winGlobal =
    (typeof window !== 'undefined' && (window.API_BASE_URL || (window.ENV && window.ENV.API_BASE_URL))) || null;
  let base = ls || winGlobal || process.env.REACT_APP_API_URL || '/api';
  // If base points to localhost but we're not on localhost, prefer same-origin '/api'
  try {
    if (typeof window !== 'undefined') {
      const host = window.location.hostname || '';
      const isLocalHost = /^(localhost|127\.0\.0\.1)$/i.test(host);
      if (!isLocalHost && /^https?:\/\/(localhost|127\.0\.0\.1)/i.test(String(base))) {
        base = `${window.location.origin}/api`;
      }
    }
  } catch {}
  return base;
};
const API_BASE_URL = computeApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Normalize website URLs to include protocol
const normalizeWebsite = (url) => {
  try {
    if (!url) return url;
    const trimmed = String(url).trim();
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
  } catch {
    return url;
  }
};

// Auth token handling
export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
};

// Always attach latest token from localStorage
api.interceptors.request.use((config) => {
  try {
    const t = localStorage.getItem('authToken');
    if (t) {
      config.headers = config.headers || {};
      config.headers['Authorization'] = `Bearer ${t}`;
    }
  } catch {}
  return config;
});

// Initialize from localStorage
try {
  const saved = localStorage.getItem('authToken');
  if (saved) setAuthToken(saved);
} catch {}

// 401 interceptor (do not auto-redirect to avoid loops; surface error to pages)
api.interceptors.response.use(
  (res) => res,
  (err) => {
    try {
      const status = err?.response?.status;
      if (status === 401) {
        // Expired or invalid token: clear and redirect to login
        try { localStorage.removeItem('authToken'); } catch {}
        setAuthToken(null);
        // Avoid showing noisy toasts; navigate immediately
        if (typeof window !== 'undefined' && window.location) {
          window.location.replace('/login');
        }
        // Swallow the error to prevent pages from showing generic "failed" warnings
        return new Promise(() => {});
      }
    } catch {}
    return Promise.reject(err);
  }
);

// Firearm Types
export const firearmTypesAPI = {
  getAll: () => api.get('/firearm-types'),
  create: (data) => api.post('/firearm-types', data),
};

// Firearms
export const firearmsAPI = {
  getAll: () => api.get('/firearms'),
  getById: (id) => api.get(`/firearms/${id}`),
  create: (data) => api.post('/firearms', data),
  update: (id, data) => api.put(`/firearms/${id}`, data),
};

// Component Types
export const componentTypesAPI = {
  getAll: () => api.get('/component-types'),
  create: (data) => api.post('/component-types', data),
};

// Components
export const componentsAPI = {
  getAll: () => api.get('/components'),
  getByType: (typeId) => api.get(`/components/by-type/${typeId}`),
  getForRecipe: () => api.get('/components/for-recipe'),
  create: (data) => api.post('/components', data),
  update: (id, data) => api.put(`/components/${id}`, data),
  remove: (id) => api.delete(`/components/${id}`),
  getCaseBatches: (componentId) => api.get(`/components/${componentId}/case-batches`),
  getCaseRemovals: (componentId) => api.get(`/components/${componentId}/case-removals`),
};

// Cartridges
export const cartridgesAPI = {
  add: (data) => api.post('/cartridges', data),
};

// Caliber Options
export const caliberOptionsAPI = {
  getAll: () => api.get('/caliber-options'),
};

// Cartridge abbreviations: display short labels (e.g., 9mm Luger -> 9mm)
export const cartridgeAbbrev = (full) => getCartridgeAbbrev(full);

export const cartridgesStatic = () => CARTRIDGES;

// Purchases
export const purchasesAPI = {
  getAll: () => api.get('/purchases'),
  create: (data) => {
    const payload = { ...data, website: normalizeWebsite(data?.website) };
    return api.post('/purchases', payload);
  },
  update: (id, data) => {
    const payload = { ...data, website: normalizeWebsite(data?.website) };
    return api.put(`/purchases/${id}`, payload);
  },
};

// Case Removals
export const caseRemovalsAPI = {
  create: (data) => api.post('/case-removals', data),
};

// Recipes
export const recipesAPI = {
  getAll: () => api.get('/recipes'),
  create: (data) => api.post('/recipes', data),
  update: (id, data) => api.put(`/recipes/${id}`, data),
  exportPdf: (id) => api.get(`/recipes/${id}/export-pdf`, { responseType: 'blob' }),
  remove: (id) => api.delete(`/recipes/${id}`),
  getCost: (id) => api.get(`/recipes/${id}/cost`),
  toggleFavorite: (id, is_favorite) => api.post(`/recipes/${id}/favorite`, typeof is_favorite === 'boolean' ? { is_favorite } : {}),
  getVelocityStats: (ids) => {
    const list = Array.isArray(ids) ? ids.filter((id) => id !== undefined && id !== null) : [];
    const params = list.length ? `?ids=${list.join(',')}` : '';
    return api.get(`/recipes/velocity-stats${params}`);
  },
};

// Loads
export const loadsAPI = {
  getAll: () => api.get('/loads'),
  create: (data) => api.post('/loads', data),
  getCaseBatches: (recipeId) => api.get(`/recipes/${recipeId}/case-batches`),
};

// Firing Sessions
export const firingSessionsAPI = {
  getAll: () => api.get('/firing-sessions'),
  create: (data) => api.post('/firing-sessions', data),
  update: (id, data) => api.put(`/firing-sessions/${id}`, data),
  importVelocities: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/firing-sessions/import-velocities', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Analytics
export const analyticsAPI = {
  getCostByCaliber: () => api.get('/analytics/cost-by-caliber'),
  getCostByFirearm: () => api.get('/analytics/cost-by-firearm'),
  getBrassStatus: () => api.get('/analytics/brass-status'),
};

// Initialize
export const initializeAPI = {
  defaultData: () => api.post('/initialize'),
  reinitialize: () => api.post('/reinitialize'),
};

// Uploads
export const uploadsAPI = {
  upload: (entityType, entityId, files) => {
    const form = new FormData();
    form.append('entity_type', entityType);
    form.append('entity_id', String(entityId));
    files.forEach((f) => form.append('files', f));
    return api.post('/uploads', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  list: (entityType, entityId) => api.get(`/uploads?entity_type=${encodeURIComponent(entityType)}&entity_id=${encodeURIComponent(entityId)}`),
  /**
   * Batch fetch images for multiple entities in one API call.
   * @param {Array<{entity_type: string, entity_id: number}>} queries
   * @returns {Promise<{data: {[key: string]: Array}}>} Map of "entity_type:entity_id" -> images array
   */
  batchList: (queries) => api.post('/uploads/batch', { queries }),
  remove: (imageId) => api.delete(`/uploads/${imageId}`),
  fetchBlob: (imageId) => api.get(`/uploads/${imageId}/download`, { responseType: 'blob' }),
  fetchThumbnail: (imageId) => api.get(`/uploads/${imageId}/thumbnail`, { responseType: 'blob' }),
  fetchPdfPreview: (imageId) => api.get(`/uploads/${imageId}/pdf-preview`, { responseType: 'blob' }),
  thumbnailPreview: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/uploads/thumbnail-preview', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob',
    });
  },
};

// Auth
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
};

export const usersAPI = {
  list: () => api.get('/users'),
  listVisible: (q) => api.get(`/users/visible${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  create: (data) => api.post('/users', { ...data, website: normalizeWebsite(data?.website) }),
  delete: (id) => api.delete(`/users/${id}`),
  resetPassword: (id, new_password) => api.post(`/users/${id}/reset-password`, { new_password }),
};

export const messagesAPI = {
  list: () => api.get('/messages'),
  send: (recipient_user_id, content, recipe_id, parent_message_id) => api.post('/messages', { recipient_user_id, content, recipe_id, parent_message_id }),
  markRead: (message_id) => api.post(`/messages/${message_id}/read`),
  markReadBulk: (message_ids = [], conv_message_ids = []) => api.post('/messages/mark-read-bulk', { message_ids, conv_message_ids }),
  cloneFromMessage: (message_id, component_map) => api.post(`/messages/${message_id}/clone`, { component_map }),
  getRecipe: (message_id) => api.get(`/messages/${message_id}/recipe`),
};

// Group Conversations (Group Chat)
export const conversationsAPI = {
  create: (member_user_ids, content, recipe_id) => api.post('/conversations', { member_user_ids, content, recipe_id }),
  upsert: (member_user_ids) => api.post('/conversations/upsert', { member_user_ids }),
  listWithMessages: () => api.get('/conversations/with-messages'),
  sendMessage: (conversation_id, content, parent_message_id, recipe_id) => api.post(`/conversations/${conversation_id}/messages`, { content, parent_message_id, recipe_id }),
  markRead: (conversation_id, message_id) => api.post(`/conversations/${conversation_id}/messages/${message_id}/read`),
  leave: (conversation_id) => api.post(`/conversations/${conversation_id}/leave`),
};

export const publicAPI = {
  getUser: (userId) => api.get(`/users/${userId}/public`),
  getRecipe: (userId, recipeId) => api.get(`/users/${userId}/recipes/${recipeId}`),
  cloneRecipe: (userId, recipeId, component_map) => api.post(`/users/${userId}/recipes/${recipeId}/clone`, { component_map }),
};

export default api;